import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlarmClock,
  Ambulance,
  Anchor,
  Antenna,
  BatteryFull,
  Bolt,
  Box,
  Building2,
  Cable,
  Cctv,
  CircleAlert,
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRainWind,
  CloudSnow,
  Compass,
  Construction,
  Container,
  Folder,
  Fuel,
  HardHat,
  Hospital,
  Landmark,
  LifeBuoy,
  MapPin,
  MapPinned,
  Navigation,
  Package,
  RadioTower,
  Route,
  ShieldAlert,
  Ship,
  ShipWheel,
  Siren,
  Stethoscope,
  Timer,
  TriangleAlert,
  Waves,
  Wind,
} from 'lucide-react';

export const objectCategoryIconMap = {
  // Domain defaults
  wind: Wind,
  cable: Cable,
  ship: Ship,

  // Offshore / rescue / navigation
  anchor: Anchor,
  'ship-wheel': ShipWheel,
  'radio-tower': RadioTower,
  antenna: Antenna,
  compass: Compass,
  navigation: Navigation,
  route: Route,
  waves: Waves,
  'life-buoy': LifeBuoy,

  // Medical / emergency
  hospital: Hospital,
  ambulance: Ambulance,
  stethoscope: Stethoscope,
  siren: Siren,
  'triangle-alert': TriangleAlert,
  'circle-alert': CircleAlert,
  'shield-alert': ShieldAlert,
  cctv: Cctv,

  // Weather
  cloud: Cloud,
  'cloud-rain-wind': CloudRainWind,
  'cloud-lightning': CloudLightning,
  'cloud-fog': CloudFog,
  'cloud-snow': CloudSnow,

  // Sites / infrastructure
  landmark: Landmark,
  'building-2': Building2,
  container: Container,
  construction: Construction,
  'hard-hat': HardHat,

  // Ops / utilities
  box: Box,
  package: Package,
  fuel: Fuel,
  bolt: Bolt,
  'battery-full': BatteryFull,
  activity: Activity,
  'alarm-clock': AlarmClock,
  timer: Timer,
  'map-pin': MapPin,
  'map-pinned': MapPinned,
} as const satisfies Record<string, LucideIcon>;

export type ObjectCategoryIconKey = keyof typeof objectCategoryIconMap;

const legacyAliases: Partial<Record<string, ObjectCategoryIconKey>> = {
  // Previously used internal keys (keep backwards compatibility)
  platform: 'landmark',
  clinic: 'hospital',
  island: 'map-pin',
  land: 'map-pinned',
  radio: 'radio-tower',
} as const;

export const normalizeObjectCategoryIconKey = (
  icon: string | null | undefined
): ObjectCategoryIconKey | null => {
  if (!icon) return null;
  if (icon in objectCategoryIconMap) return icon as ObjectCategoryIconKey;
  return legacyAliases[icon] ?? null;
};

export const objectCategoryIconOptions: Array<{
  key: ObjectCategoryIconKey;
  Icon: LucideIcon;
}> = Object.entries(objectCategoryIconMap).map(([key, Icon]) => ({
  key: key as ObjectCategoryIconKey,
  Icon,
}));

export const getObjectCategoryIcon = (icon: string | null | undefined): LucideIcon => {
  const normalized = normalizeObjectCategoryIconKey(icon);
  if (!normalized) return Folder;
  return objectCategoryIconMap[normalized] ?? Folder;
};
